import * as React from "react"
import { isScratchReferenceVideoUrl, type YoutubeFormat } from "@nexus/contracts"
import { Button, Dialog, DialogContent, DialogTitle, Icon, cn } from "@nexus/ui"
import { useTranslation } from "react-i18next"

import { HubScriptSetPicker, type HubScriptSetPickerProps } from "../../../shared/HubScriptSetPicker/HubScriptSetPicker.js"
import { isSelectableHubScriptSetId } from "../../../../lib/hub-script-target.js"
import {
  YOUTUBE_FORMAT_OFF,
  YoutubeFormatField,
  type YoutubeFormatChoice,
} from "../../generate-scripts/YoutubeFormatField.js"

const MAX_DESCRIPTION = 10000
const VARIATION_OPTIONS = [1, 2, 3, 4, 5] as const

export interface PersonaSuggestScriptModalProps {
  open: boolean
  onClose: () => void
  onSubmit: (
    description: string,
    variations: number,
    referenceVideoUrl?: string,
    youtubeFormat?: YoutubeFormat,
  ) => void
  /** Brief hides the variation picker and asks for one brief. */
  outputKind?: "script" | "brief"
  /** Render the form without a dialog shell. */
  embedded?: boolean
  isSubmitting?: boolean
  error?: string | null
  setId: string
  /** Hub: pick or create the target set inside this dialog. Omitted in the single-set workspace. */
  setPicker?: HubScriptSetPickerProps
  /** Keep this dialog open while the create-set dialog is on top. */
  suspendDismiss?: boolean
}

function keepDialogMounted(suspend: boolean | undefined) {
  return (event: { preventDefault: () => void }) => {
    if (suspend) event.preventDefault()
  }
}

export function PersonaSuggestScriptModal({
  open,
  onClose,
  onSubmit,
  isSubmitting = false,
  error,
  setId,
  setPicker,
  suspendDismiss = false,
  outputKind = "script",
  embedded = false,
}: PersonaSuggestScriptModalProps) {
  const { t } = useTranslation()
  const [description, setDescription] = React.useState("")
  const [variations, setVariations] = React.useState(3)
  const [referenceVideoUrl, setReferenceVideoUrl] = React.useState("")
  const [youtubeFormat, setYoutubeFormat] = React.useState<YoutubeFormatChoice>(YOUTUBE_FORMAT_OFF)
  const textareaRef = React.useRef<HTMLTextAreaElement>(null)

  React.useEffect(() => {
    if (open) {
      setDescription("")
      setVariations(3)
      setReferenceVideoUrl("")
      setYoutubeFormat(YOUTUBE_FORMAT_OFF)
      requestAnimationFrame(() => textareaRef.current?.focus())
    }
  }, [open])

  const trimmedVideoUrl = referenceVideoUrl.trim()
  const hasVideoUrl = trimmedVideoUrl.length > 0
  const videoUrlIsValid = !hasVideoUrl || isScratchReferenceVideoUrl(trimmedVideoUrl)
  const hasSet = isSelectableHubScriptSetId(setId)
  const canSubmit = description.trim().length > 0 && videoUrlIsValid && !isSubmitting && hasSet
  const selectedYoutubeFormat = youtubeFormat === YOUTUBE_FORMAT_OFF ? undefined : youtubeFormat

  function handleSubmit() {
    const trimmed = description.trim()
    if (!trimmed || !videoUrlIsValid || isSubmitting || !hasSet) return
    const count = outputKind === "brief" ? 1 : variations
    if (selectedYoutubeFormat) {
      onSubmit(trimmed, count, hasVideoUrl ? trimmedVideoUrl : undefined, selectedYoutubeFormat)
      return
    }
    if (hasVideoUrl) {
      onSubmit(trimmed, count, trimmedVideoUrl)
      return
    }
    onSubmit(trimmed, count)
  }

  const body = (
    <>
        {embedded ? null : (
        <div className="flex items-center gap-3 border-b border-border/60 px-6 py-5">
          <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-[var(--ai-fill-chip)]">
            <Icon name="sparkles" size="sm" className="text-[var(--ai-text)]" />
          </div>
          <DialogTitle className="text-base font-semibold leading-snug">
            {t("personaOpportunities.scripts.suggest.title", "Generate Script")}
          </DialogTitle>
        </div>
        )}

        {setPicker && !embedded ? <HubScriptSetPicker {...setPicker} /> : null}

          <div className="flex flex-col gap-4 px-6 py-5">
            <p className="text-sm text-muted-foreground">
              {t(
                "personaOpportunities.scripts.suggest.description",
                "Describe the script you want and how many variations to generate. We'll use your brand context to keep it on-brand.",
              )}
            </p>

            <textarea
              ref={textareaRef}
              value={description}
              onChange={(e) => setDescription(e.target.value.slice(0, MAX_DESCRIPTION))}
              placeholder={t(
                "personaOpportunities.scripts.suggest.placeholder",
                'Describe the script you want — e.g. "A 15-second UGC unboxing highlighting the waterproof zipper"',
              )}
              aria-label={t(
                "personaOpportunities.scripts.suggest.descriptionAria",
                "Script description",
              )}
              rows={4}
              disabled={isSubmitting}
              className={cn(
                "w-full resize-none rounded-lg border bg-transparent px-3 py-2.5 text-sm outline-none transition-[color,box-shadow]",
                "placeholder:text-muted-foreground/50",
                "focus:border-[var(--ai)] focus:ring-[3px] focus:ring-[var(--ai-fill-chip)]",
                "disabled:cursor-not-allowed disabled:opacity-50",
                error ? "border-destructive" : "border-input",
              )}
            />

            <div className="flex flex-col gap-2">
              <label htmlFor="scratch-reference-video" className="text-sm font-medium text-foreground">
                {t("personaOpportunities.scripts.suggest.referenceVideoLabel", "Reference video (optional)")}
              </label>
              <p id="scratch-reference-video-help" className="text-xs text-muted-foreground">
                {t(
                  "personaOpportunities.scripts.suggest.referenceVideoHelp",
                  "Paste one Instagram, TikTok, or YouTube link. We'll match that clip's structure.",
                )}
              </p>
              <input
                id="scratch-reference-video"
                type="url"
                value={referenceVideoUrl}
                onChange={(e) => setReferenceVideoUrl(e.target.value)}
                placeholder={t("personaOpportunities.scripts.suggest.referenceVideoPlaceholder", "https://…")}
                disabled={isSubmitting}
                aria-invalid={!videoUrlIsValid}
                aria-describedby={
                  videoUrlIsValid
                    ? "scratch-reference-video-help"
                    : "scratch-reference-video-help scratch-reference-video-error"
                }
                className={cn(
                  "w-full rounded-md border bg-transparent px-3 py-2 text-sm outline-none",
                  "disabled:cursor-not-allowed disabled:opacity-50",
                  videoUrlIsValid ? "border-input" : "border-destructive",
                )}
              />
              {videoUrlIsValid ? null : (
                <p id="scratch-reference-video-error" className="text-xs text-destructive">
                  {t(
                    "personaOpportunities.scripts.suggest.referenceVideoInvalid",
                    "Use an Instagram, TikTok, or YouTube link.",
                  )}
                </p>
              )}
            </div>

            <YoutubeFormatField
              value={youtubeFormat}
              onChange={setYoutubeFormat}
              disabled={isSubmitting}
            />

            {outputKind === "script" ? <div className="flex items-center justify-between">
              <span className="text-xs text-muted-foreground">
                {t("personaOpportunities.scripts.suggest.variationsLabel", "Variations")}
              </span>
              <div className="flex items-center gap-1.5">
                {VARIATION_OPTIONS.map((n) => (
                  <button
                    key={n}
                    type="button"
                    onClick={() => setVariations(n)}
                    disabled={isSubmitting}
                    aria-pressed={variations === n}
                    className={cn(
                      "h-8 w-8 rounded-md border text-sm tabular-nums transition-colors disabled:opacity-50",
                      variations === n
                        ? "border-primary/40 bg-primary/5 text-foreground"
                        : "border-border text-muted-foreground hover:bg-muted hover:text-foreground",
                    )}
                  >
                    {n}
                  </button>
                ))}
              </div>
            </div> : null}

            {error && (
              <p className="flex items-center gap-1.5 text-xs text-destructive">
                <Icon name="alert-circle" size="xs" />
                {error}
              </p>
            )}
          </div>

        <div className="flex items-center justify-end gap-2 border-t border-border/60 px-6 py-4">
              <Button variant="outline" size="sm" onClick={onClose}>
                {t("common.cancel", "Cancel")}
              </Button>
              <Button
                size="sm"
                iconStart={isSubmitting ? "loader" : "sparkles"}
                onClick={handleSubmit}
                disabled={!canSubmit}
                className={isSubmitting ? "[&_svg]:animate-spin" : ""}
              >
                {outputKind === "brief"
                    ? t("personaOpportunities.scripts.suggest.submitBrief", "Generate brief")
                    : t("personaOpportunities.scripts.suggest.submit", {
                        defaultValue: "Generate {{count}}",
                        count: variations,
                      })}
              </Button>
        </div>
    </>
  )

  if (embedded) return open ? body : null

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v && suspendDismiss) return; if (!v) onClose() }}>
        <DialogContent
          className="sm:max-w-2xl gap-0 p-0"
          aria-describedby={undefined}
          onInteractOutside={keepDialogMounted(suspendDismiss)}
          onPointerDownOutside={keepDialogMounted(suspendDismiss)}
          onFocusOutside={keepDialogMounted(suspendDismiss)}
          onEscapeKeyDown={keepDialogMounted(suspendDismiss)}
        >
        {body}
      </DialogContent>
    </Dialog>
  )
}
